import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Input,
  message,
  Space,
  Tag,
} from 'antd';
import { EditOutlined, SearchOutlined, SendOutlined } from '@ant-design/icons';
import { TechnicalSupportManagementPageModel } from '../pageModels/TechnicalSupportManagementPageModel';
import { RedisConfigItemDTO } from '../../apis/TechSupport';
import '../styles/TechnicalSupportManagement.css';

const TechnicalSupportManagement: React.FC = observer(() => {
  const [pageModel] = useState(() => new TechnicalSupportManagementPageModel());
  const [form] = Form.useForm();

  useEffect(() => {
    pageModel.loadRedisConfigs();
  }, [pageModel]);

  const showEditModal = (config: RedisConfigItemDTO) => {
    pageModel.showEditModal(config);
    form.setFieldsValue({ value: Number(config.value) });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await pageModel.savePraiseWallMinPrice(values.value);
      message.success('修改成功');
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || '修改失败');
    }
  };

  const handleQuerySmsCode = async () => {
    try {
      await pageModel.querySmsCodeStatus();
    } catch (error: any) {
      message.error(error?.message || '查询失败');
    }
  };

  const handleSendSmsCode = async () => {
    try {
      await pageModel.sendSmsCode();
      message.success('验证码已发放');
      if (pageModel.smsQueryPhone.trim() === pageModel.smsSendPhone.trim()) {
        await pageModel.querySmsCodeStatus();
      }
    } catch (error: any) {
      message.error(error?.message || '发放失败');
    }
  };

  const columns = [
    {
      title: '配置项',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      onHeaderCell: () => ({ style: { paddingLeft: 0 } }),
      onCell: () => ({ style: { paddingLeft: 0 } }),
    },
    {
      title: 'Redis Key',
      dataIndex: 'key',
      key: 'key',
      width: 280,
    },
    {
      title: '当前值',
      dataIndex: 'value',
      key: 'value',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      onHeaderCell: () => ({ style: { paddingRight: 0 } }),
      onCell: () => ({ style: { paddingRight: 0 } }),
      render: (_: unknown, record: RedisConfigItemDTO) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            style={{ padding: '4px 8px' }}
          >
            修改
          </Button>
        </Space>
      ),
    },
  ];

  const smsCodePanel = (
    <div className="tech-support-sms-panel">
      <div className="tech-support-sms-section">
        <div className="tech-support-sms-section-title">查询验证码</div>
        <Space wrap size="middle">
          <Input
            placeholder="请输入手机号"
            value={pageModel.smsQueryPhone}
            onChange={(e) => pageModel.setSmsQueryPhone(e.target.value)}
            style={{ width: 240, borderRadius: 0 }}
            maxLength={20}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={pageModel.smsCodeLoading}
            onClick={handleQuerySmsCode}
            style={{ borderRadius: 0 }}
          >
            查询
          </Button>
        </Space>
        {pageModel.smsCodeStatus && (
          <div className="tech-support-sms-result">
            <div className="tech-support-sms-result-row">
              <span className="tech-support-sms-label">手机号</span>
              <span>{pageModel.smsCodeStatus.phoneNumber}</span>
            </div>
            <div className="tech-support-sms-result-row">
              <span className="tech-support-sms-label">验证码</span>
              {pageModel.smsCodeStatus.expired ? (
                <Tag color="default">已过期</Tag>
              ) : (
                <span className="tech-support-sms-code">{pageModel.smsCodeStatus.verificationCode}</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="tech-support-sms-section">
        <div className="tech-support-sms-section-title">发放验证码</div>
        <Space wrap size="middle">
          <Input
            placeholder="请输入手机号"
            value={pageModel.smsSendPhone}
            onChange={(e) => pageModel.setSmsSendPhone(e.target.value)}
            style={{ width: 240, borderRadius: 0 }}
            maxLength={20}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={pageModel.smsSendLoading}
            onClick={handleSendSmsCode}
            style={{ borderRadius: 0 }}
          >
            发放
          </Button>
        </Space>
      </div>
    </div>
  );

  const tabItems = [
    {
      key: 'redis',
      label: 'Redis配置项',
      children: (
        <Table
          className="tech-support-table"
          columns={columns}
          dataSource={pageModel.redisConfigs}
          rowKey="key"
          loading={pageModel.loading}
          pagination={false}
          size="middle"
        />
      ),
    },
    {
      key: 'sms',
      label: '验证码管理',
      children: smsCodePanel,
    },
  ];

  return (
    <div className="tech-support-page">
      <Tabs
        activeKey={pageModel.activeTab}
        onChange={(key) => pageModel.setActiveTab(key)}
        items={tabItems}
      />

      <Modal
        title="修改口碑墙最低价"
        open={pageModel.isEditModalVisible}
        onOk={handleSave}
        onCancel={() => pageModel.hideEditModal()}
        okText="保存"
        cancelText="取消"
        width={480}
        styles={{
          content: { borderRadius: 0 },
          header: { borderRadius: 0 },
          footer: { borderRadius: 0 },
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="value"
            label="口碑墙最低价（元）"
            rules={[
              { required: true, message: '请输入金额' },
              { type: 'number', min: 0, message: '金额不能为负数' },
            ]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%', borderRadius: 0 }}
              placeholder="请输入订单金额下限"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export default TechnicalSupportManagement;
